/**
 * Hook for fetching and comparing build status
 *
 * Provides:
 * - Current build info (from environment/package.json)
 * - Latest deployed build info (from backend)
 * - Whether we're on the latest build
 * - Fresh deployment status
 */

import { useEffect, useState } from 'react'

/**
 * Fetch and compare current build against latest deployed
 *
 * Usage:
 *   const { isLatest, latestBuild, minutesSince, loading, error } = useBuildStatus()
 *
 *   if (loading) return <div>Loading build info...</div>
 *   if (error) return <div>⚠️ (build info unavailable)</div>
 *
 *   return (
 *     <div>
 *       {isLatest ? '✓ Latest' : '⟳ Update available'}
 *       {minutesSince !== null && ` (${minutesSince}m ago)`}
 *     </div>
 *   )
 */
export function useBuildStatus() {
  const [status, setStatus] = useState({
    isLatest: null,
    latestBuild: null,
    minutesSince: null,
    error: null,
    loading: true,
  })

  useEffect(() => {
    const fetchBuildStatus = async () => {
      try {
        // Get current build info from environment
        const currentBuild = {
          build_id: import.meta.env.VITE_BUILD_ID || 'unknown',
          package_version: import.meta.env.VITE_APP_VERSION || '0.0.0',
          environment: import.meta.env.VITE_ENVIRONMENT || 'dev',
        }

        // Query backend for latest build
        const response = await fetch('/api/builds/status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(currentBuild),
        })

        if (!response.ok) {
          throw new Error(`Backend returned ${response.status}`)
        }

        const data = await response.json()

        setStatus({
          isLatest: data.is_latest,
          latestBuild: data.latest_build,
          minutesSince: data.minutes_since_deployment,
          error: null,
          loading: false,
        })
      } catch (err) {
        // Log error for debugging, but don't crash
        console.warn('Build status check failed:', err instanceof Error ? err.message : String(err))
        setStatus(prev => ({
          ...prev,
          error: err instanceof Error ? err : new Error(String(err)),
          loading: false,
        }))
      }
    }

    fetchBuildStatus()

    // Optionally refresh every 5 minutes
    const interval = setInterval(fetchBuildStatus, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  return status
}

